class ProductSizeGuide extends HTMLElement {
  connectedCallback() {
    if (this.sizeGuideInitialized) return;

    this.dialog = this.querySelector('[data-size-guide-dialog]');
    this.scrollContainer = this.querySelector('.product-size-guide-modal__scroll');
    this.closeButton = this.querySelector('[data-size-guide-close]');
    this.tabs = Array.from(this.querySelectorAll('[data-size-guide-tab-button]'));
    this.panels = Array.from(this.querySelectorAll('[data-size-guide-panel]'));

    if (!this.dialog) return;

    this.sizeGuideInitialized = true;
    this.onDocumentClick = (event) => {
      const opener = event.target?.closest
        ? event.target.closest('[data-size-guide-open]')
        : event.target;

      if (!opener || opener.getAttribute?.('aria-controls') !== this.dialog.id) return;
      this.open(opener);
    };
    document.addEventListener('click', this.onDocumentClick);

    this.closeButton?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('close', () => this.onClose());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.sizeGuideTabButton));
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event));
    });
  }

  disconnectedCallback() {
    document.removeEventListener?.('click', this.onDocumentClick);
    if (this.dialog?.open) document.documentElement.classList.remove('product-size-guide-is-open');
  }

  getOpeners() {
    return Array.from(document.querySelectorAll('[data-size-guide-open]')).filter(
      (opener) => opener.getAttribute('aria-controls') === this.dialog.id
    );
  }

  open(opener) {
    this.lastOpener = opener;
    this.selectTab(opener.dataset.sizeGuideTab);
    this.getOpeners().forEach((control) => control.setAttribute('aria-expanded', 'true'));
    document.documentElement.classList.add('product-size-guide-is-open');

    if (!this.dialog.open) this.dialog.showModal();

    window.requestAnimationFrame(() => {
      if (this.scrollContainer) this.scrollContainer.scrollTop = 0;
      this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.focus({ preventScroll: true });
    });
  }

  onClose() {
    const openers = this.getOpeners();
    openers.forEach((control) => control.setAttribute('aria-expanded', 'false'));
    document.documentElement.classList.remove('product-size-guide-is-open');
    const visibleLastOpener = this.lastOpener?.getClientRects().length ? this.lastOpener : null;
    const equivalentVisibleOpener = openers.find(
      (opener) =>
        opener.dataset.sizeGuideTab === this.lastOpener?.dataset.sizeGuideTab && opener.getClientRects().length
    );
    (visibleLastOpener || equivalentVisibleOpener)?.focus();
  }

  selectTab(tabName) {
    if (!this.tabs.some((tab) => tab.dataset.sizeGuideTabButton === tabName)) return;

    this.tabs.forEach((tab) => {
      const isSelected = tab.dataset.sizeGuideTabButton === tabName;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.sizeGuidePanel !== tabName;
    });
  }

  onTabKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;

    const nextTab = this.tabs[nextIndex];
    this.selectTab(nextTab.dataset.sizeGuideTabButton);
    nextTab.focus({ preventScroll: true });
  }
}

if (!customElements.get('product-size-guide')) {
  customElements.define('product-size-guide', ProductSizeGuide);
}
